import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { EMPTY_QUOTE, QUOTE_STORAGE_KEY, QUOTE_VERSION, type Quote } from './types'

type Ctx = {
  quote: Quote
  setQuote: (next: Quote | ((prev: Quote) => Quote)) => void
  reset: () => void
}

const QuoteContext = createContext<Ctx | null>(null)

function read(): Quote {
  if (typeof window === 'undefined') return { ...EMPTY_QUOTE }
  try {
    const raw = window.sessionStorage.getItem(QUOTE_STORAGE_KEY)
    if (!raw) return { ...EMPTY_QUOTE }
    const parsed = JSON.parse(raw) as Partial<Quote>
    if (parsed.version !== QUOTE_VERSION) {
      window.sessionStorage.removeItem(QUOTE_STORAGE_KEY)
      return { ...EMPTY_QUOTE }
    }
    return { ...EMPTY_QUOTE, ...parsed, version: QUOTE_VERSION }
  } catch {
    return { ...EMPTY_QUOTE }
  }
}

function write(q: Quote) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(q))
  } catch {
    // sessionStorage may be unavailable in private windows; non-fatal.
  }
}

export function QuoteProvider({ children }: { children: ReactNode }) {
  const [quote, setQuoteState] = useState<Quote>(() => read())

  useEffect(() => {
    write(quote)
  }, [quote])

  const setQuote = useCallback((next: Quote | ((prev: Quote) => Quote)) => {
    setQuoteState((prev) => (typeof next === 'function' ? (next as (p: Quote) => Quote)(prev) : next))
  }, [])

  const reset = useCallback(() => {
    setQuoteState({ ...EMPTY_QUOTE })
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(QUOTE_STORAGE_KEY)
    }
  }, [])

  return <QuoteContext.Provider value={{ quote, setQuote, reset }}>{children}</QuoteContext.Provider>
}

export function useQuote(): Ctx {
  const ctx = useContext(QuoteContext)
  if (!ctx) throw new Error('useQuote must be used inside <QuoteProvider>')
  return ctx
}
